import { cn } from "@/utils/cn";
import { cva } from "class-variance-authority";
import { cloneElement, isValidElement } from "react";

const buttonVariants = cva(
    "flex w-fit cursor-pointer justify-center gap-1 border-0 whitespace-nowrap text-white outline-0 transition-colors focus-visible:shadow-[0_0_0_0.25rem] focus-visible:outline-2 focus-visible:-outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>svg]:shrink-0",
    {
        variants: {
            variant: {
                primary:
                    "bg-darkgreen-500 shadow-darkgreen-500/25 outline-darkgreen-400 hover:bg-darkgreen-400 active:bg-darkgreen-300 disabled:bg-darkgreen-500",
                "primary-outline":
                    "j bg-transparent text-darkgreen-500 shadow-darkgreen-500/25 outline-1 -outline-offset-1 outline-darkgreen-400 hover:bg-darkgreen-400 hover:text-white active:bg-darkgreen-300 active:outline-darkgreen-300 disabled:bg-darkgreen-500/10",
                secondary:
                    "bg-neutral-200 text-darkgreen-500 shadow-neutral-200/25 outline-neutral-300 hover:bg-neutral-300 active:bg-neutral-400 disabled:bg-neutral-200",
                success:
                    "bg-emerald-950 text-darkgreen-500 shadow-emerald-950/25 outline-emerald-800 hover:bg-emerald-800 active:bg-emerald-700 disabled:bg-emerald-950",
                warning:
                    "bg-orange-800 text-black shadow-orange-600/25 outline-orange-700 hover:bg-orange-700 active:bg-orange-800 disabled:bg-orange-600",
                danger: "bg-red-500 shadow-red-500/25 outline-red-600 hover:bg-red-400 active:bg-red-300 disabled:bg-red-500",
                link: "p-0! text-sm font-bold text-darkgreen-500 no-underline hover:underline",
            },
            size: {
                default:
                    "rounded-3xl px-6 py-3 text-base font-semibold [&>svg]:size-6",
                sm: "rounded-3xl px-4 py-2 text-sm [&>svg]:size-5",
                lg: "rounded-4xl px-8 py-4 text-lg font-semibold [&>svg]:size-7",
            },
        },
        compoundVariants: [
            {
                variant: "primary",
                gradient: true,
                className:
                    "bg-linear-to-r/oklch from-darkgreen-500 to-darkgreen-400 hover:from-darkgreen-400 hover:to-darkgreen-300 active:from-darkgreen-300 active:to-darkgreen-200 disabled:from-darkgreen-500 disabled:to-darkgreen-400",
            },
        ],
        defaultVariants: {
            variant: "primary",
            size: "default",
        },
    },
);

export const Button = ({
    children,
    className,
    variant,
    size,
    gradient,
    asChild = false,
    ...props
}) => {
    const classes = cn(buttonVariants({ variant, size, gradient }), className);

    if (asChild && isValidElement(children)) {
        return cloneElement(children, {
            className: cn(children.props.className, classes),
            ...props,
        });
    }

    return (
        <button className={classes} {...props}>
            {children}
        </button>
    );
};
